import { HttpResponse } from '@angular/common/http';

import { ExportEntity } from '../../entities/downloads/export-entity.entity';
import { ExportJob } from '../../entities/downloads/export-job.entity';
import { DelenProgress } from './export-melding.helper';
import { classifyExportStatus, ExportStatusClass } from './export-status.helper';

/**
 * What the create call handed back. `created`: the delen exist and carry an id. `accepted` (202): the backend
 * could not confirm the delen in time and only knows the names they will get, so they are looked up by name.
 */
export type CreateOutcome =
    | { readonly kind: 'created'; readonly jobs: ExportJob[] }
    | { readonly kind: 'accepted'; readonly names: string[] };

export function toCreateOutcome(response: HttpResponse<ExportJob[]>): CreateOutcome {
    const jobs: ExportJob[] = response.body ?? [];

    if (response.status === 202 || jobs.some(job => !job.Id)) {
        return { kind: 'accepted', names: jobs.map(job => job.Name).filter(name => !!name) };
    }

    return { kind: 'created', jobs };
}

/** How far the named delen are, read from the downloads list; a name not listed yet counts as open. */
export function toAcceptedProgress(names: string[], exports: ExportEntity[]): DelenProgress {
    const byName: Map<string, ExportEntity> = new Map(exports.map(entry => [toNameKey(entry.Name), entry]));
    const classes: ExportStatusClass[] = names.map(name => {
        const entry: ExportEntity | undefined = byName.get(toNameKey(name));

        return entry ? classifyExportStatus(entry.Status) : 'open';
    });

    return {
        total: names.length,
        done: classes.filter(status => status === 'klaar').length,
        failed: classes.filter(status => status === 'mislukt').length,
        open: classes.filter(status => status === 'open').length,
        slow: false
    };
}

/** The listed export of every name, in the order of the names; `null` while any name is not listed yet. */
export function toNamedExports(names: string[], exports: ExportEntity[]): ExportEntity[] | null {
    const byName: Map<string, ExportEntity> = new Map(exports.map(entry => [toNameKey(entry.Name), entry]));
    const found: (ExportEntity | undefined)[] = names.map(name => byName.get(toNameKey(name)));

    return found.every(entry => !!entry) ? (found as ExportEntity[]) : null;
}

/** Waits longer between polls the longer the delen take: every 5s the first minute, 15s up to 5 minutes, then 30s. */
export function toAcceptedPollDelay(elapsed: number): number {
    if (elapsed < 60 * 1000) {
        return 5000;
    }

    if (elapsed < 5 * 60 * 1000) {
        return 15000;
    }

    return 30000;
}

function toNameKey(name: string | null | undefined): string {
    return (name ?? '').trim().toLowerCase();
}
